import { AppIcon, visuallyHiddenSx } from '@backrs_ui/common-ui';

import {
  dayBadge,
  formatLongDate,
  formatWeekday,
} from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { PlanDay } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, Button, Chip, SxProps, Theme, Tooltip, Typography } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.header;

const TOGGLE_BLOCKED_ID = 'booster-day-complete-blocked-reason';

type DayHeaderProps = Readonly<{
  day: PlanDay;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onToggleComplete: () => void;
  toggleDisabled: boolean;
  /** When set, the toggle is blocked and this is the reason shown on hover or focus. */
  toggleBlockedReason?: string;
}>;

/**
 * Day-detail header: a date badge with the full date, weekday, and Week-N chip, plus prev/next/Today
 * navigation and the completion toggle. Selecting a different date swaps the day, not the tab title.
 */
export const DayHeader = ({
  day,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onToday,
  onToggleComplete,
  toggleDisabled,
  toggleBlockedReason,
}: DayHeaderProps) => {
  const badge = dayBadge(day.date);
  const completed = day.completed;
  // An empty reason would disable the toggle while leaving nothing to explain it.
  const blockedReason = toggleBlockedReason || undefined;

  return (
    <Box sx={rootSx} id="booster-day-header">
      <Box sx={identitySx}>
        <Box sx={badgeSx} aria-hidden>
          <Box sx={badgeMonthSx}>
            <Typography variant="text-xs-semibold" sx={badgeMonthTextSx}>
              {badge.month}
            </Typography>
          </Box>
          <Box sx={badgeDaySx}>
            <Typography variant="text-lg-bold" sx={badgeDayTextSx}>
              {badge.day}
            </Typography>
          </Box>
        </Box>
        <Box sx={textColSx}>
          <Box sx={dateRowSx}>
            <Typography variant="text-lg-semibold" sx={dateSx}>
              {formatLongDate(day.date)}
            </Typography>
            <Chip size="sm" variant="badge" label={STRINGS.weekChip(day.week)} sx={weekChipSx} />
          </Box>
          <Typography variant="text-sm-regular" sx={weekdaySx}>
            {formatWeekday(day.date)}
          </Typography>
        </Box>
      </Box>

      <Box sx={actionsSx}>
        <Box sx={navGroupSx}>
          <Button
            id="booster-day-prev-btn"
            sx={navArrowSx}
            disabled={!canPrev}
            onClick={onPrev}
            aria-label={STRINGS.prevDayAria}
          >
            <AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />
          </Button>
          <Button id="booster-day-today-btn" sx={navTodaySx} onClick={onToday}>
            {STRINGS.today}
          </Button>
          <Button
            id="booster-day-next-btn"
            sx={navArrowSx}
            disabled={!canNext}
            onClick={onNext}
            aria-label={STRINGS.nextDayAria}
          >
            <AppIcon iconName="ArrowRight" diameter="20px" color="currentColor" />
          </Button>
        </Box>

        {/* A disabled button takes neither pointer events nor focus, so the span carries the
            hover target and the tab stop. An empty title renders nothing. */}
        <Tooltip title={blockedReason ?? ''} describeChild>
          <Box component="span" sx={toggleWrapSx} tabIndex={blockedReason ? 0 : undefined}>
            <Button
              id="booster-day-complete-toggle"
              hierarchy="Secondary color"
              size="md"
              icon="Leading"
              disabled={toggleDisabled || blockedReason !== undefined}
              onClick={onToggleComplete}
              aria-label={STRINGS.toggleAria}
              aria-describedby={blockedReason ? TOGGLE_BLOCKED_ID : undefined}
              aria-pressed={completed}
              startIcon={
                <AppIcon
                  iconName={completed ? 'CheckCircle' : 'ClockFastForward'}
                  diameter="20px"
                  color="currentColor"
                />
              }
              sx={completed ? completePillSx : undefined}
            >
              {completed ? STRINGS.complete : STRINGS.incomplete}
            </Button>
            {blockedReason && (
              <Box component="span" id={TOGGLE_BLOCKED_ID} sx={visuallyHiddenSx}>
                {blockedReason}
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  paddingInline: backrs.spacing['spacing-3xl'],
  paddingBlock: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Beige/950']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const identitySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 0,
});

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  width: '64px',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const badgeMonthSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  paddingTop: backrs.spacing['spacing-xs'],
  paddingBottom: backrs.spacing['spacing-xxs'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const badgeMonthTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  lineHeight: 1.5,
});

const badgeDaySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  paddingTop: backrs.spacing['spacing-xxs'],
  paddingBottom: backrs.spacing['spacing-sm'],
});

const badgeDayTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  lineHeight: 1,
});

const textColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  minWidth: 0,
});

const dateRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const dateSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const weekChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  height: 'auto',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    color: backrs.colors['Colors/Text/text-secondary'],
    paddingInline: backrs.spacing['spacing-sm'],
    paddingBlock: backrs.spacing['spacing-xxs'],
  },
});

const weekdaySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
});

const toggleWrapSx: SxProps<Theme> = {
  display: 'inline-flex',
};

const navGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'stretch',
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  overflow: 'hidden',
  boxShadow: backrs.shadows['shadow-xs'],
});

const navArrowSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 0,
  minHeight: backrs.spacing['spacing-5xl'],
  paddingInline: backrs.spacing['spacing-lg'],
  paddingBlock: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  borderRadius: 0,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&:last-of-type': { borderRight: 'none' },
  '&.Mui-disabled': { color: backrs.colors['Colors/Text/text-disabled'] },
});

const navTodaySx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  minWidth: 0,
  minHeight: backrs.spacing['spacing-5xl'],
  paddingInline: backrs.spacing['spacing-xl'],
  paddingBlock: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-secondary'],
  borderRadius: 0,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  textTransform: 'none',
});

// Completed swaps the "Secondary color" (brand) pill to success tones; incomplete uses the
// hierarchy as-is.
const completePillSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-success-primary'],
  borderColor: backrs.colors['Colors/Foreground/fg-success-secondary'],
  backgroundColor: backrs.colors['Colors/Background/bg-success-primary'],
  '&:hover': {
    color: backrs.colors['Colors/Text/text-success-primary'],
    backgroundColor: backrs.colors['Colors/Background/bg-success-secondary'],
  },
});
